import { useQuery } from '@apollo/client'
import { useNavigate, useParams } from '@faceup/router'
import { HtmlRenderer, usePageTemplate } from '@faceup/ui'
import { Skeleton, Typography } from '@faceup/ui-base'
import { convertBasenameToLanguage, isUUIDv7 } from '@faceup/utils'
import { useContext, useEffect } from 'react'
import { graphql } from '../../__generated__'
import { MenuItems } from '../../Components/MenuItems/MenuItems'
import { LanguageContext } from '../../Contexts/LanguageContext'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { ReportFormContext } from '../../Contexts/ReportFormContext'
import { UserContext } from '../../Contexts/UserContext'
import { useReportMenuItems } from '../../hooks/useReportMenuItems'
import { PageNotAvailable } from './Components/PageNotAvailable'
import PageNotFound from './Components/PageNotFound'
import DefaultPageRedirect from './DefaultPageRedirect'

const query = graphql(`
  query PageViewQuery(
    $id: UUID!
    $reportSourceId: PublicReportSourceGlobalId!
    $institutionId: UUID!
  ) {
    page(pageId: $id) {
      id
      content
      language
      title
    }
    publicReportSource(
      reportSourceId: $reportSourceId
      institutionId: $institutionId
    ) {
      id
      name
      isEnabled
      isDeleted
      isAnonymityEnabled
      isDefault
      sourceType
      institution {
        id
        billing {
          id
          plan
        }
      }
    }
  }
`)

const PageView = () => {
  const { id } = useParams<{ id: string }>()
  const isPageIdUUIDv7 = isUUIDv7(id ?? '')
  const { fillObject, setIsDefaultForm } = usePageTemplate()
  const { reportSource } = useContext(UserContext)
  const navigate = useNavigate()
  const { basename } = useContext(LanguageContext)
  const { client } = useContext(LayoutContext)
  const { setIsAnonymous } = useContext(ReportFormContext)

  const isApp = client === 'app'

  const { data, loading } = useQuery(query, {
    variables: {
      id: id ?? '',
      reportSourceId: reportSource?.defaultFormId ?? '',
      institutionId: reportSource?.institutionId ?? '',
    },
    skip: !reportSource?.defaultFormId || !isPageIdUUIDv7,
  })

  const page = data?.page
  const reportSourceData = data?.publicReportSource
  const reportMenuItems = useReportMenuItems({
    plan: reportSourceData?.institution?.billing?.plan,
  })

  useEffect(() => {
    const isPageLoaded = page !== undefined
    const doesPageCorrespondsWithLanguage = convertBasenameToLanguage(basename) === page?.language
    if (isPageLoaded && !doesPageCorrespondsWithLanguage) {
      navigate(routes => routes.page())
    }
  }, [basename, navigate, page])

  useEffect(() => {
    if (typeof reportSourceData?.isAnonymityEnabled === 'boolean') {
      setIsAnonymous(reportSourceData.isAnonymityEnabled)
    }
  }, [reportSourceData?.isAnonymityEnabled, setIsAnonymous])

  useEffect(() => {
    setIsDefaultForm(reportSourceData?.isDefault ?? false)
  }, [reportSourceData?.isDefault, setIsDefaultForm])

  const isDeleted = reportSourceData?.isDeleted
  const isEnabled = reportSourceData?.isEnabled

  if (!isPageIdUUIDv7) {
    // some users might have a link to a page with old base64 encoded id
    // redirect them to the default page
    return <DefaultPageRedirect />
  }

  if (data && (!page || isDeleted)) {
    return <PageNotFound />
  }

  if (data && !isEnabled) {
    return <PageNotAvailable />
  }

  return (
    <div>
      {loading || !page ? (
        <div className='flex flex-col gap-8'>
          <Typography.Title className='text-center'>
            <Skeleton />
          </Typography.Title>
          <Skeleton />
        </div>
      ) : (
        <div className='w-full'>
          <div
            data-test='pages-content'
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
            }}
          >
            <HtmlRenderer html={fillObject(page).content} />
          </div>
          <div className='hidden sm:block'>
            {reportSourceData && (
              <div className={`sticky bottom-0 ${isApp ? 'hidden' : ''}`}>
                <div
                  style={{
                    background: '#fff',
                    paddingBlock: 16,
                    borderTop: `1px solid #9DB0DF1A`,
                  }}
                >
                  <MenuItems items={reportMenuItems} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PageView
